import { useState } from "react";

/**
 * Robust image with initials fallback.
 * If `src` fails to load (404, broken URL, missing file), shows a gradient
 * background with the subject's initials so the layout never breaks.
 *
 * Re-keyed on `src` so a new src starts a fresh load attempt.
 *
 * Usage:
 *   <Avatar src={artist.image} name={artist.name} className="absolute inset-0 w-full h-full" />
 */
export default function Avatar(props) {
  return <AvatarInner key={props.src || "no-src"} {...props} />;
}

function AvatarInner({ src, name = "", className = "", imgClassName = "object-cover w-full h-full", alt }) {
  const [failed, setFailed] = useState(false);

  const initials = (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

  if (!src || failed) {
    return (
      <div
        className={`flex items-center justify-center text-white/40 ${className}`}
        style={{ background: "linear-gradient(135deg, #1a1a1d 0%, #0a0a0c 100%)" }}
        data-fallback="initials"
      >
        <span className="font-display font-black text-4xl tracking-tight select-none">
          {initials}
        </span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt || name}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${className} ${imgClassName}`.trim()}
    />
  );
}
